import React, { FC, useMemo } from 'react';
import { useTranslate } from '@im/react-localization';
import {
  SearchBar,
  Props as SearchBarProps,
} from 'src/components/AutoSearch/AutoSearch';
import {
  CustomerSearchFilters,
  useCustomerSearchQuery,
  useCustomerQueryParams,
  Customer,
} from '../../../hooks/customerSearchQuery';

export interface CustomerFilter {
  field: keyof Customer;
  value: string;
}

export interface CustomerSearchBarProps {
  includeCustomer: (customer: Customer) => boolean;
  onSelected: (customer: Customer) => void;
}

export type { Customer };

const CustomerSearchBar: FC<CustomerSearchBarProps> = ({
  includeCustomer,
  onSelected,
}) => {
  const translate = useTranslate();
  const options = useMemo(
    () =>
      CustomerSearchFilters.map((value) => ({
        label: translate(`CustomerGroups.${value}`),
        value,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate, translate('CustomerGroups.customerName')]
  );

  const { filter, searchQuery, setFilter } = useCustomerQueryParams({
    pageSize: 200,
  });

  const { data: { list: customers } = {}, isLoading } =
    useCustomerSearchQuery(searchQuery);

  const searchBarProps: SearchBarProps = {
    autoCompleteProps: {
      filterOptions: (opts: any) => opts,
      getOptionLabel: (v: any) =>
        typeof v === 'string' ? v : `${v.customerNumber} - ${v.customerName}`,
      loading: isLoading,
      loadingText: translate('Common.Loading'),
      onChange: (_: any, customer: Customer, reason: string) => {
        if (reason === 'selectOption') {
          onSelected(customer);
        }
      },
      options: (customers || []).filter(includeCustomer) as any,
    },
    dropdownOptions: options,
    onDropdownOptionChange: (field) =>
      setFilter({
        field: field?.value as unknown as keyof Customer,
        value: '',
      }),
    onSearch: ({ dropdownTerm: field, inputValue: value }) =>
      setFilter({ field: field as unknown as keyof Customer, value }),
    value: filter?.value,
  };

  return <SearchBar {...searchBarProps} />;
};

export default CustomerSearchBar;
